import React, { useCallback, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { FiUser, FiLock } from 'react-icons/fi';
import {
  useGoogleLogin,
  useGoogleOneTapLogin,
} from '@react-oauth/google';
import liwazaLogo from '../assets/liwaza-logo.png';
import googleLogo from '../assets/google-logo.png'; // Make sure this exists
import './Authentication.css';
import DeviceRecommendationModal from '../components/DeviceRecommendationModal';
import {
  API_BASE_URL,
  GOOGLE_CLIENT_ID,
  consumeStoredLoginRedirectMessage,
  consumeStoredPostLoginRedirect,
  toastdisplay,
  login,
  storeUserSession,
  detectDevice,
  showDeviceRecommendation,
  redirectBasedOnPremiumStatus,
} from '../utils/utils';
import { getErrorMessage } from '../utils/getErrorMessage';
import { decodeCoachId } from '../utils/hashids';
import {
  normalizeCountryCode,
  normalizeDisplayCurrency,
  subscriptionAmounts,
} from '../pricing/pricingStripeConfig';
import { claimWebinarRegistrationIfPending } from '../core/webinars/webinarPublicApi';
import { hasAuthenticatedSession } from '../utils/authSession';
import { getPaymentIntentFromStorageOrSearchParams } from '../utils/paymentRedirectIntent';
import {
  LOCAL_LOGIN_PRESETS,
  shouldShowLocalLoginPresets,
} from './localLoginPresets';
import {
  learnerAppAccessDeniedMessage,
  redirectToStaffConsole,
} from '../utils/staffAppAccess';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });
  const [searchParams] = useSearchParams();
  const coach_id = (() => {
    const coachIdParam = searchParams.get('coach_id');
    if (!coachIdParam) return null;
    try {
      return decodeCoachId(coachIdParam);
    } catch (error) {
      console.error('Invalid coach_id hash:', error);
      return null;
    }
  })();

  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [deviceType, setDeviceType] = useState('desktop');
  const [oneTapDisabled, setOneTapDisabled] = useState(() =>
    hasAuthenticatedSession()
  );
  const redirectAfterLoginRef = useRef(() => {});
  const showLocalPresets = shouldShowLocalLoginPresets();

  const readStoredJson = (key) => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.error(`Error parsing ${key}:`, error);
      return null;
    }
  };

  const readSessionUser = () => {
    try {
      const raw = sessionStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.error('Error parsing session user:', error);
      return null;
    }
  };

  // Show message from navigation state (e.g., when redirected from a post link)
  React.useEffect(() => {
    const redirectMessage =
      location.state?.message || consumeStoredLoginRedirectMessage();
    if (redirectMessage) {
      toastdisplay(redirectMessage, false, 4000, setToast);
    }
  }, [location.state]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleRedirectAfterLogin = useCallback(() => {
    // Check if user was trying to access a specific page before login (highest priority).
    // We navigate immediately to avoid stacking another 2s timer on top of the
    // welcome toast that already delayed us — the previous "double timer" pattern
    // created a wide race window where another guard could redirect the user
    // back to /login before this navigate fired.
    const redirectAfterLogin = consumeStoredPostLoginRedirect();
    if (redirectAfterLogin) {
      toastdisplay(
        'Redirecting to your requested content...',
        true,
        1500,
        setToast
      );
      navigate(redirectAfterLogin, { replace: true });
      return;
    }

    // Check if there's a specific redirect URL from the navigation state (e.g., direct post link)
    const redirectTo = location.state?.redirectTo;
    if (redirectTo) {
      toastdisplay(
        'Redirecting to your requested content...',
        true,
        1500,
        setToast
      );
      navigate(redirectTo, { replace: true });
      return;
    }

    const intent = getPaymentIntentFromStorageOrSearchParams(searchParams);

    // Check for payment redirect context from storage or query params
    if (intent?.action === 'subscribe') {
      toastdisplay('Proceeding to payment...', true, 2000, setToast);
      setTimeout(() => {
        handlePaymentRedirect(intent);
      }, 2000);
      return;
    }

    // Get user data to check first-time status
    const user = readSessionUser();

    // Check if user came from coach profile
    const fromCoachProfile = localStorage.getItem('fromCoachProfile');
    const pendingCoachAction = localStorage.getItem('pendingCoachAction');

    if (fromCoachProfile === 'true' && pendingCoachAction) {
      console.log('fromCoachProfile', fromCoachProfile);
      console.log('pendingCoachAction', pendingCoachAction);
      // Redirect to pricing page to show both cards
      const coachIdParam = searchParams.get('coach_id');
      if (coachIdParam) {
        navigate(`/pricing?coach_id=${coachIdParam}`);
      } else {
        localStorage.removeItem('fromCoachProfile');
        localStorage.removeItem('pendingCoachAction');
        navigate('/pricing');
      }
    } else if (
      user &&
      user.role?.name !== 'coach' &&
      !user.is_premium &&
      user.is_first_time === false &&
      !user.had_premium_before &&
      user.user_type !== 'premium_lapsed' &&
      !user.is_premium_lapsed
    ) {
      // If user is not premium, not first-time, and never had premium before, redirect to community
      navigate('/community');
    } else {
      // Use regular redirect flow for premium users, first-time users, or users who had premium before
      redirectBasedOnPremiumStatus(navigate, coach_id);
    }
  }, [coach_id, location.state?.redirectTo, navigate, searchParams]);
  redirectAfterLoginRef.current = handleRedirectAfterLogin;

  const handlePaymentRedirect = (intentOverride = null) => {
    const intent =
      intentOverride || getPaymentIntentFromStorageOrSearchParams(searchParams);
    let planKey = 'monthly';
    let billingCurrency = 'eur';
    let productLine = 'graduate_full';

    const user = readSessionUser();

    if (intent) {
      planKey = intent.plan || 'monthly';
      if (planKey === '11months') planKey = '12months';
      billingCurrency =
        (intent.billingCurrency || 'eur').toLowerCase() === 'usd'
          ? 'usd'
          : 'eur';
      if (intent.productLine === 'executive') productLine = 'executive';
      else if (intent.productLine === 'families') productLine = 'families';
      else if (intent.productLine === 'graduate_ielts')
        productLine = 'graduate_ielts';
      else if (intent.productLine === 'graduate_full')
        productLine = 'graduate_full';
      else if (intent.productType === 'executive-mba')
        productLine = 'executive';
      else if (intent.productType === 'families') productLine = 'families';
    }

    if (productLine === 'executive') {
      planKey = planKey === 'complete' ? 'complete' : '3months';
    }

    const displayCurrency = normalizeDisplayCurrency(
      intent?.displayCurrency || intent?.display_currency,
      billingCurrency === 'usd' ? 'USD' : 'EUR'
    );

    let pricingSegment = 'standard';
    if (user?.pricing_segment === 'legacy_graduate') {
      pricingSegment = 'legacy_graduate';
    } else if (intent?.pricing_segment === 'legacy_graduate') {
      pricingSegment = 'legacy_graduate';
    }

    const snap = subscriptionAmounts({
      productLine,
      billingCurrency,
      planKey,
      pricingSegment,
      displayCurrency,
      countryCode: normalizeCountryCode(intent?.country_code),
    });

    if (!user?.email) {
      toastdisplay(
        'Please sign in again before continuing to payment.',
        false,
        3000,
        setToast
      );
      return;
    }

    if (!snap.stripeUrl) {
      toastdisplay(
        'Payment link configuration is missing for this plan. Please contact support.',
        false,
        4000,
        setToast
      );
      return;
    }

    localStorage.setItem(
      'pending_subscription',
      JSON.stringify({
        user_email: user.email,
        timestamp: Date.now(),
        price_eur: snap.amountEur,
        price: snap.displayAmount,
        billing_price: snap.chargeAmount,
        billing_currency: billingCurrency.toUpperCase(),
        currency: snap.displayCurrency,
        display_currency: snap.displayCurrency,
        country_code: intent?.country_code || null,
        currency_symbol: snap.currencySymbol,
        plan: planKey,
        product_line: productLine,
        pricing_segment: pricingSegment,
      })
    );

    const paymentUrl = new URL(snap.stripeUrl);
    paymentUrl.searchParams.set('prefilled_email', user.email);
    localStorage.removeItem('payment_intent');
    window.location.href = paymentUrl.toString();
  };

  const performLogin = async (loginEmail, loginPassword) => {
    setOneTapDisabled(true);

    if (!loginEmail || !validateEmail(loginEmail)) {
      toastdisplay(
        'Please enter a valid email address.',
        false,
        3000,
        setToast
      );
      return;
    }

    if (!loginPassword) {
      toastdisplay('Please enter your password.', false, 3000, setToast);
      return;
    }

    try {
      const response = await login(loginEmail, loginPassword, coach_id);
      if (response.staffOnly) {
        toastdisplay(
          response.message || learnerAppAccessDeniedMessage(),
          false,
          6000,
          setToast
        );
        setTimeout(() => redirectToStaffConsole(), 2500);
        return;
      }
      if (response.success) {
        await claimWebinarRegistrationIfPending();
        // Check if user came from payment intent before showing generic message
        const paymentIntent = localStorage.getItem('payment_intent');
        if (paymentIntent) {
          toastdisplay(`Proceeding to payment...`, true, 2000, setToast);
        } else {
          toastdisplay(
            `Welcome back, ${response.user.first_name}!`,
            true,
            2000,
            setToast
          );
        }

        // Check device and show recommendation if needed
        const currentDevice = detectDevice();
        setDeviceType(currentDevice);

        if (showDeviceRecommendation(currentDevice, setToast)) {
          setShowDeviceModal(true);
          setTimeout(() => {
            setShowDeviceModal(false);
            handleRedirectAfterLogin();
          }, 5000);
        } else {
          setTimeout(() => handleRedirectAfterLogin(), 2000);
        }
      } else {
        toastdisplay(response.message, false, 3000, setToast);
      }
    } catch (error) {
      console.error('Login error:', error);
      toastdisplay(
        'An error occurred. Please try again.',
        false,
        3000,
        setToast
      );
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    await performLogin(email, password);
  };

  const handleLocalPresetLogin = async (preset) => {
    setEmail(preset.email);
    setPassword(preset.password);
    await performLogin(preset.email, preset.password);
  };

  // Update Google login success handlers to also use the new redirect function
  const onGoogleSuccess = useCallback(async (credentialResponse) => {
    try {
      setOneTapDisabled(true);
      const token =
        credentialResponse.credential || credentialResponse.access_token;
      // Ensure we have the required token field
      const response = await fetch(`${API_BASE_URL}/api/google-login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token, // The required token field
          credential: credentialResponse.credential,
          coach_id,
          referral_coach_id: coach_id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const detail = getErrorMessage(data?.detail ?? data, 'Google login failed.');
        toastdisplay(detail, false, detail.includes('/admin') ? 6000 : 2000, setToast);
        if (response.status === 403 && String(detail).includes('/admin')) {
          setTimeout(() => redirectToStaffConsole(), 2500);
        }
        return;
      }

      await storeUserSession(data.user, data.token);
      await claimWebinarRegistrationIfPending();

      // Detect device and show recommendation for mobile users
      const detectedDeviceType = detectDevice();
      setDeviceType(detectedDeviceType);

      if (detectedDeviceType === 'mobile') {
        setShowDeviceModal(true);
      } else {
        // Check if user came from payment intent before showing generic message
        const paymentIntent = localStorage.getItem('payment_intent');
        if (paymentIntent) {
          toastdisplay('Proceeding to payment...', true, 2000, setToast);
        } else {
          toastdisplay(
            data.message || 'Login successful.',
            true,
            2000,
            setToast
          );
        }
        setTimeout(() => redirectAfterLoginRef.current(), 2000);
      }
    } catch (error) {
      console.error('Google login error:', error);
      toastdisplay(
        'Google login failed. Please try again.',
        false,
        3000,
        setToast
      );
    }
  }, [coach_id]);

  const googleOneTapLogin = useCallback(async (credentialResponse) => {
    console.log('Google credential response:', credentialResponse);

    try {
      setOneTapDisabled(true);
      // Ensure we have the required token field

      const response = await fetch(`${API_BASE_URL}/api/google-one-tap-login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credential: credentialResponse.credential,
          clientId: GOOGLE_CLIENT_ID,
          coach_id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const detail = getErrorMessage(data?.detail ?? data, 'Google login failed.');
        toastdisplay(detail, false, detail.includes('/admin') ? 6000 : 2000, setToast);
        if (response.status === 403 && String(detail).includes('/admin')) {
          setTimeout(() => redirectToStaffConsole(), 2500);
        }
        return;
      }

      await storeUserSession(data.user, data.token);
      await claimWebinarRegistrationIfPending();

      // Detect device and show recommendation for mobile users
      const detectedDeviceType = detectDevice();
      setDeviceType(detectedDeviceType);

      if (detectedDeviceType === 'mobile') {
        setShowDeviceModal(true);
      } else {
        // Check if user came from payment intent before showing generic message
        const paymentIntent = localStorage.getItem('payment_intent');
        if (paymentIntent) {
          toastdisplay('Proceeding to payment...', true, 2000, setToast);
        } else {
          toastdisplay(
            data.message || 'Login successful.',
            true,
            2000,
            setToast
          );
        }
        setTimeout(() => redirectAfterLoginRef.current(), 2000);
      }
    } catch (err) {
      setOneTapDisabled(true);
      toastdisplay('Error during Google login.', false, 2000, setToast);
    }
  }, [coach_id]);

  const handleGoogleOneTapError = useCallback(() => {
    setOneTapDisabled(true);
    console.warn('Google One Tap Sign-In was dismissed or aborted.');
  }, []);

  // Integrate Google One Tap login
  useGoogleOneTapLogin({
    onSuccess: googleOneTapLogin,
    onError: handleGoogleOneTapError,
    disabled: oneTapDisabled || hasAuthenticatedSession(),
  });

  // Inside your Signup component:
  const googleLogin = useGoogleLogin({
    onSuccess: onGoogleSuccess,
    onError: () => toastdisplay('Google Sign-In failed', false, 2000, setToast),
  });

  const handleDeviceModalClose = () => {
    setShowDeviceModal(false);
    // Check if user came from payment intent before showing generic message
    const paymentIntent = localStorage.getItem('payment_intent');
    if (paymentIntent) {
      toastdisplay('Proceeding to payment...', true, 2000, setToast);
    } else {
      toastdisplay('Welcome to Liwaza!', true, 2000, setToast);
    }
    setTimeout(() => handleRedirectAfterLogin(), 2000);
  };

  return (
    <div className="liwaza-layout">
        <div style={{ width: '100%', maxWidth: 400, marginBottom: '16px' }}>
          <img
            src={liwazaLogo}
            alt="Liwaza Logo"
            style={{ height: '32px', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          />
        </div>

        <div className="liwaza-login-container">
          <form className="liwaza-form" onSubmit={handleLogin}>
            <h1 className="liwaza-title">Sign in to Liwaza</h1>

            <div className="liwaza-field">
              <label htmlFor="email">Email address</label>
              <div className="liwaza-input-wrapper">
                <FiUser className="liwaza-icon" />
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@email.com"
                />
              </div>
            </div>

            <div className="liwaza-field">
              <label htmlFor="password">Password</label>
              <div className="liwaza-input-wrapper">
                <FiLock className="liwaza-icon" />
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Your password"
                />
              </div>
            </div>

            <div className="liwaza-login-footer">
              <a href="/forgotpassword" className="liwaza-login-link">
                Forgot password?
              </a>
            </div>

            <button className="btn-lwz-primary" type="submit">
              Sign in
            </button>

            <button
              type="button"
              className="btn-lwz-outline"
              onClick={() => {
                let signupUrl = '/signup';
                const params = new URLSearchParams(searchParams);

                if (params.toString()) {
                  signupUrl += '?' + params.toString();
                }

                navigate(signupUrl);
              }}
            >
              Create an account
            </button>
          </form>

          {/* Google Sign In - right side */}
          <div className="google-login-column">
            <div className="google-login-title">or</div>
            <div className="google-login-column">
              <button
                className="btn-lwz-primary"
                onClick={() => {
                  setOneTapDisabled(true);
                  googleLogin();
                }}
              >
                <img
                  src={googleLogo}
                  alt="Google"
                  style={{ width: 20, marginRight: 8 }}
                />
                Sign in with Google
              </button>
            </div>
          </div>
        </div>

        {showLocalPresets && (
          <div className="local-login-presets">
            <p className="local-login-presets-title">Local test accounts</p>
            <p className="local-login-presets-hint">
              Visible on localhost only. Seeded in the backend SQLite DB.
            </p>
            <div className="local-login-presets-grid">
              {LOCAL_LOGIN_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  className="local-login-preset"
                  onClick={() => handleLocalPresetLogin(preset)}
                >
                  <span className="local-login-preset-emoji">{preset.emoji}</span>
                  <span className="local-login-preset-label">{preset.label}</span>
                  <span className="local-login-preset-detail">{preset.detail}</span>
                  <span className="local-login-preset-email">{preset.email}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <a href="/terms" className="liwaza-legal-link">
            Terms and conditions
          </a>
        </div>

        {toast.show && (
          <div
            className={`toast-notification ${toast.success ? 'success' : 'error'}`}
          >
            <span className="toast-icon">{toast.success ? '✔️' : '❌'}</span>
            <span>{toast.message}</span>
          </div>
        )}

        <DeviceRecommendationModal
          isOpen={showDeviceModal}
          onClose={handleDeviceModalClose}
          deviceType={deviceType}
        />
      </div>
  );
};

export default Login;
